import React, { useEffect } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import meetingRoomsImg from "assets/images/testMeetingRooms.webp";
import NoDataFound from "components/common/NoDataFound";
import { DollarSign, MapPin, UserRound } from "lucide-react";
import tvIcon from "assets/images/icons/temp/tv.svg";
import wifiIcon from "assets/images/icons/temp/wifi.svg";
import gardenIcon from "assets/images/icons/temp/garden.svg";
import deliveryIcon from "assets/images/icons/temp/delivery.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getWorkspaceDetails } from "store/actions/workspaceSlice";
import Spinners from "components/common/Spinners";
import { formatTime } from "components/common/Utils";
import { InputSwitch } from "primereact/inputswitch";
import riyalGray2 from "assets/images/icons/riyalGray2.svg";
import Riyal from "assets/images/icons/riyal.svg";

const MeetingRoomDetails = () => {
  const { t } = useTranslation();
  document.title = t("Meeting Details | Work Space");
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const dispatch = useDispatch();

  const { loading, workspaceDetails } = useSelector((state) => state.Workspace);

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };
    getDetails();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content common_details_page meeting_room_details_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Meeting Room Details")} />
            </Row>
            <div className="common_wrapper">
              <Row>
                <Col lg={6} xxl={7} className="images_section">
                  <div className="d-flex gap-3">
                    <div className="left_section">
                      {workspaceDetails?.imageUrls?.[0] && (
                        <img
                          src={workspaceDetails.imageUrls[0]}
                          alt="Main Image"
                        />
                      )}
                    </div>
                    <div
                      className="right_section"
                      style={{
                        gridTemplateColumns:
                          workspaceDetails?.imageUrls?.length <= 3
                            ? "1fr"
                            : "repeat(2, 1fr)",
                      }}
                    >
                      {workspaceDetails?.imageUrls
                        ?.slice(1)
                        .map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt={`Workspace Image ${index + 2}`}
                          />
                        ))}
                    </div>
                  </div>
                </Col>
                <Col lg={6} xxl={5}>
                  <div className="opening_time">
                    <h2 className="fs_20">{t("Opening Time")}</h2>
                    <div className="days_section">
                      {workspaceDetails?.openingHours?.length === 0 ? (
                        <NoDataFound className="h_fit_content" />
                      ) : (
                        <Table>
                          <thead>
                            <tr>
                              <th>{t("Day Of Week")}</th>
                              <th>{t("Allow 24 Open")}</th>
                              {/* <th>{t("Closed")}</th> */}
                              <th>{t("Time")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workspaceDetails?.openingHours?.map((day) => (
                              <tr key={day?._id} className="single_day">
                                <td>
                                  <p>{day?.dayOfWeek?.slice(0, 3)}</p>
                                </td>
                                <td>
                                  <p>
                                    <InputSwitch
                                      checked={day.allowed24Hour}
                                      onChange={(e) => {}}
                                      disabled={true}
                                    />
                                  </p>
                                </td>
                                {/* <td>
                                  <p>
                                    <InputSwitch
                                      checked={day?.closed}
                                      onChange={(e) => {}}
                                      disabled={true}
                                    />
                                  </p>
                                </td> */}
                                <td>
                                  {day?.allowed24Hour || day?.closed ? (
                                    "-"
                                  ) : (
                                    <p>
                                      {day?.openingTime} - {day?.closingTime}
                                    </p>
                                  )}{" "}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </div>
                </Col>
                <Col lg={12} className="mt-3">
                  <div className="title_section">
                    <h2 className="fs_24">
                      {workspaceDetails?.name},{" "}
                      {workspaceDetails?.nameAr || "-"}
                    </h2>
                    <p className="mt-3 fs_14 text_gray d-flex align-items-center gap-4">
                      <span>
                        <UserRound
                          className="text_gray me-1"
                          width={14}
                          height={14}
                        />
                        {workspaceDetails?.meetingRoomDetails?.numberOfSeats}{" "}
                        {t("Seats")}
                      </span>
                      <span>
                        <MapPin
                          className="text_gray me-1"
                          width={14}
                          height={14}
                        />
                        {workspaceDetails?.cityId?.name}
                        {", "}
                        {workspaceDetails?.branchId?.name}
                      </span>
                      <span>
                        <img
                          src={riyalGray2}
                          width={15}
                          height={15}
                          alt="riyal"
                          className="me-1"
                        />
                        {workspaceDetails?.meetingRoomDetails?.price}{" "}
                      </span>
                    </p>
                  </div>
                </Col>
                <Col lg={12} className="mt-3">
                  <div className="description_row">
                    <h2 className="fs_20">{t("Description")}</h2>
                    <div className="description_inner">
                      <div className="w-50 me-1">
                        <p>{workspaceDetails?.description || "-"}</p>
                      </div>
                      <div className="divider"></div>
                      <div className="w-50 rtl ms-1">
                        <p>{workspaceDetails?.descriptionAr || "-"}</p>
                      </div>
                    </div>
                  </div>
                </Col>
                {workspaceDetails?.customRates?.length > 0 && (
                  <Col lg={6} xxl={5}>
                    <div className="opening_time custom_price mt-3">
                      <h2 className="fs_20 mb-3">{t("Custom Price")}</h2>
                      <div className="days_section">
                        <Table>
                          <thead>
                            <tr>
                              <th>{t("Day Name")}</th>
                              <th>{t("Start Time")}</th>
                              <th>{t("End Time")}</th>
                              <th>{t("Price")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workspaceDetails?.customRates?.map((item) => (
                              <tr key={item?._id} className="single_day">
                                <td>
                                  <p>{item?.dayName}</p>
                                </td>
                                <td>
                                  <p>{item?.startTime}</p>
                                </td>
                                <td>
                                  <p>{item?.endTime}</p>
                                </td>
                                <td>
                                  <p className="d-flex ">
                                    <img
                                      src={Riyal}
                                      width={12}
                                      height={12}
                                      alt="riyal"
                                      className="me-1"
                                    />
                                    {item?.rate}
                                  </p>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Col>
                )}
                <Col lg={12} className="mt-3">
                  <div className="amenities_row">
                    <h2 className="fs_20">{t("Amenities")}</h2>
                    <div className="amenities_inner">
                      {workspaceDetails?.amenities?.length === 0 ? (
                        <div className="b_r_20 bg-white w-100 pb-3">
                          <NoDataFound className="h_fit_content" />
                        </div>
                      ) : (
                        workspaceDetails?.amenities?.map((amenity) => (
                          <div className="single_amenity" key={amenity?._id}>
                            <img
                              src={amenity?.imageUrl}
                              alt={amenity?.name}
                              width={24}
                              height={24}
                            />
                            <p className="fs_14">{amenity.name || "-"}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <Row>
              <div className="g_end mt_32 mb-2">
                <button
                  className="main_btn"
                  onClick={() => {
                    navigate(
                      `/bookings/meeting-rooms/update-meeting-room?id=${id}`
                    );
                  }}
                >
                  {t("Edit")}
                </button>
              </div>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default MeetingRoomDetails;
